/**
 * User Context
 *
 * Provides a strict guarantee that the user is authenticated.
 * This context halts rendering if user.id is null, preventing
 * any data leakage between sessions or tenants.
 *
 * Components wrapped in UserProvider will NEVER receive a null user.
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { User } from '@supabase/supabase-js';
import SkeletonLogin from '../components/auth/SkeletonLogin';

// Strict user type that guarantees user.id is never null
export interface StrictUser {
  id: string;
  email: string | undefined;
  fullName: string | undefined;
  avatarUrl: string | undefined;
  tenantId: string;
  role: string;
}

interface UserContextType {
  user: StrictUser;
  rawUser: User;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

/**
 * UserProvider - Strict user authentication guard
 *
 * This provider halts rendering if user.id is null, ensuring
 * that all child components have access to a valid user.
 *
 * Security: Prevents data leakage by blocking UI until valid auth.
 */
export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { user, tenant, loading, signOut } = useAuth();
  const [stuckTimer, setStuckTimer] = useState(false);
  const [slowConnection, setSlowConnection] = useState(false);

  // P1.4 FIX: Two-stage stuck detection:
  // - 10s: Show "slow connection" warning (visual cue, non-destructive)
  // - 30s: Force sign out (safety valve for truly stuck states)
  // Previous 10s force-signout was too aggressive for slow networks.
  useEffect(() => {
    if (!loading && user?.id && !tenant?.id) {
      const warningTimer = setTimeout(() => {
        if (import.meta.env.DEV) console.warn('[UserContext] Slow connection: user exists but tenant still null after 10s');
        setSlowConnection(true);
      }, 10000);

      const signoutTimer = setTimeout(() => {
        if (import.meta.env.DEV) console.error('[UserContext] Stuck state detected: user exists but tenant is null after 30s. Forcing sign out.');
        setStuckTimer(true);
      }, 30000);

      return () => {
        clearTimeout(warningTimer);
        clearTimeout(signoutTimer);
      };
    }
    // Reset if state resolves
    setStuckTimer(false);
    setSlowConnection(false);
  }, [loading, user?.id, tenant?.id]);

  // Force sign out after stuck timeout
  useEffect(() => {
    if (stuckTimer) {
      signOut();
    }
  }, [stuckTimer, signOut]);

  // Show loading skeleton while auth state is being determined
  if (loading) {
    return <SkeletonLogin />;
  }

  // CRITICAL: Halt rendering if user.id is null
  // This prevents any data from rendering without proper authentication
  if (!user?.id) {
    if (import.meta.env.DEV) console.warn('[UserContext] Halting render: user.id is null');
    return <SkeletonLogin />;
  }

  // CRITICAL: Halt rendering if tenant is null
  // This prevents cross-tenant data leakage
  if (!tenant?.id) {
    if (import.meta.env.DEV) console.warn('[UserContext] Halting render: tenant.id is null');
    return (
      <>
        <SkeletonLogin />
        {slowConnection && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-yellow-500/90 text-white px-4 py-2 rounded-lg text-sm font-medium backdrop-blur-sm shadow-lg">
            Conexión lenta — cargando perfil de usuario...
          </div>
        )}
      </>
    );
  }

  // Create strict user object with guaranteed non-null values
  const strictUser: StrictUser = {
    id: user.id,
    email: user.email,
    fullName: user.user_metadata?.full_name,
    avatarUrl: user.user_metadata?.avatar_url,
    tenantId: tenant.id,
    role: tenant.role,
  };

  const value: UserContextType = {
    user: strictUser,
    rawUser: user,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

/**
 * useUser - Access the strict user context
 *
 * This hook guarantees that user.id is never null.
 * It will throw an error if used outside of UserProvider.
 */
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

/**
 * RequireAuth - Component guard for authenticated routes
 *
 * Wraps children in UserProvider to ensure authentication.
 * This is the recommended way to guard authenticated sections.
 */
export const RequireAuth: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <UserProvider>{children}</UserProvider>;
};

export default UserContext;
